import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { menuApi } from '../../api/menuApi';
import { ActivityLedgerRow, incomeExpenseApi } from '../../api/incomeExpenseApi';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { IncomeExpensePageShell } from './components/IncomeExpensePageShell';
import { PlaceholderTabPanel } from './components/PlaceholderTabPanel';
import CategoryListTab from './components/CategoryListTab';
import ActivityLedgerTab, { ActivityLedgerTabHandle } from './components/ActivityLedgerTab';
import CreateActivityVoucherModal from './components/CreateActivityVoucherModal';
import CancelledActivityVouchersModal from './components/CancelledActivityVouchersModal';
import ActivityVoucherActionHistoryTab from './components/ActivityVoucherActionHistoryTab';
import { EXPENDITURE_ACTIVITY_TABS } from './tabConfigs';

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const ExportIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const ExpenditureActivitiesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('activities');
  const [buildingId, setBuildingId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [editLedgerEntryId, setEditLedgerEntryId] = useState<string | null>(null);
  const [allowEdit, setAllowEdit] = useState(false);
  const [allowCancel, setAllowCancel] = useState(false);
  const ledgerRef = useRef<ActivityLedgerTabHandle>(null);
  const activeConfig = EXPENDITURE_ACTIVITY_TABS.find((tab) => tab.id === activeTab);

  useEffect(() => {
    Promise.all([
      menuApi.checkPermission('IE_EXPENDITURE_ACTIVITIES', 'edit'),
      menuApi.checkPermission('IE_EXPENDITURE_ACTIVITIES', 'delete'),
    ]).then(([edit, cancel]) => {
      setAllowEdit(edit);
      setAllowCancel(cancel);
    });
  }, []);

  const openCreateModal = () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn tòa nhà trước khi tạo phiếu chi');
      return;
    }
    setShowCreateModal(true);
  };

  const handleCancelVoucher = async (row: ActivityLedgerRow) => {
    if (!window.confirm(`Huỷ phiếu ${row.voucherCode || ''}? Số tiền sẽ được hoàn vào tài khoản dự án.`)) {
      return;
    }
    try {
      await incomeExpenseApi.cancelExpenditureVoucherByLedgerEntry(row.id);
      toast.success('Đã huỷ phiếu chi');
      setRefreshToken((value) => value + 1);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể huỷ phiếu'));
    }
  };

  return (
    <>
      <IncomeExpensePageShell
        pageTitle="Hoạt động chi"
        pageCode="IE_EXPENDITURE_ACTIVITIES"
        tabs={EXPENDITURE_ACTIVITY_TABS.map(({ id, label }) => ({ id, label }))}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        actions={
          activeTab === 'categories' || activeTab === 'history'
            ? undefined
            : [
                {
                  label: 'Danh sách phiếu chi đã huỷ',
                  variant: 'secondary',
                  onClick: () => {
                    if (!buildingId) {
                      toast.error('Vui lòng chọn tòa nhà');
                      return;
                    }
                    setShowCancelledModal(true);
                  },
                },
                ...(activeTab === 'activities'
                  ? [
                      {
                        label: 'Xuất báo cáo',
                        variant: 'success' as const,
                        icon: <ExportIcon />,
                        onClick: () => ledgerRef.current?.exportReport(),
                      },
                    ]
                  : []),
                {
                  label: 'Tạo phiếu chi',
                  variant: 'primary',
                  icon: <PlusIcon />,
                  onClick: openCreateModal,
                },
              ]
        }
      >
        {activeTab === 'categories' ? (
          <CategoryListTab type="expenditure" />
        ) : activeTab === 'activities' ? (
          <ActivityLedgerTab
            ref={ledgerRef}
            kind="expenditure"
            refreshToken={refreshToken}
            onBuildingIdChange={setBuildingId}
            allowEditManualVoucher={allowEdit}
            allowCancelManualVoucher={allowCancel}
            onEditManualVoucher={(row) => setEditLedgerEntryId(row.id)}
            onCancelManualVoucher={handleCancelVoucher}
          />
        ) : activeTab === 'history' ? (
          <ActivityVoucherActionHistoryTab
            kind="expenditure"
            refreshToken={refreshToken}
            onBuildingIdChange={setBuildingId}
          />
        ) : (
          activeConfig && <PlaceholderTabPanel config={activeConfig.panel} />
        )}
      </IncomeExpensePageShell>

      {showCreateModal && buildingId && (
        <CreateActivityVoucherModal
          kind="expenditure"
          buildingId={buildingId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => setRefreshToken((value) => value + 1)}
        />
      )}

      {editLedgerEntryId && buildingId && (
        <CreateActivityVoucherModal
          kind="expenditure"
          buildingId={buildingId}
          editLedgerEntryId={editLedgerEntryId}
          onClose={() => setEditLedgerEntryId(null)}
          onCreated={() => setRefreshToken((value) => value + 1)}
          onUpdated={() => setRefreshToken((value) => value + 1)}
        />
      )}

      {showCancelledModal && buildingId && (
        <CancelledActivityVouchersModal
          kind="expenditure"
          buildingId={buildingId}
          onClose={() => setShowCancelledModal(false)}
        />
      )}
    </>
  );
};

export default ExpenditureActivitiesPage;
